import { useRef, useState } from "react";
import { useMutation } from "@apollo/client";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Select,
  Stack,
  Textarea,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { FiEdit2 } from "react-icons/fi";
import {
  ADMIN_SEND_ORGANIZATION_ONBOARDING_EMAIL,
  ADMIN_UPDATE_ORGANIZATION_NOTIFICATION_SETTINGS,
} from "../provision/graphql";

type Schedule = "IMMEDIATE" | "ON_NEXT_REQUEST_CLOSE" | "MANUAL";
type RecipientMode = "DEFAULT" | "APPEND" | "REPLACE";

type InitialSettings = {
  recipientMode?: string | null;
  additionalEmails?: string[] | null;
  onboardingSchedule?: string | null;
} | null;

const RECIPIENT_MODES: RecipientMode[] = ["DEFAULT", "APPEND", "REPLACE"];
const SCHEDULES: Schedule[] = ["IMMEDIATE", "ON_NEXT_REQUEST_CLOSE", "MANUAL"];

const toRecipientMode = (value?: string | null): RecipientMode =>
  RECIPIENT_MODES.includes(value as RecipientMode) ? (value as RecipientMode) : "DEFAULT";

const toSchedule = (value?: string | null): Schedule =>
  SCHEDULES.includes(value as Schedule) ? (value as Schedule) : "MANUAL";

const toSettingsSnapshot = (settings?: InitialSettings) => ({
  recipientMode: toRecipientMode(settings?.recipientMode),
  onboardingSchedule: toSchedule(settings?.onboardingSchedule),
  additionalEmails: (settings?.additionalEmails ?? []).join(", "),
});

export const OrganizationNotificationControls: React.FC<{
  organizationId: string;
  initialSettings?: InitialSettings;
}> = ({ organizationId, initialSettings }) => {
  const toast = useToast();
  const confirmSend = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  // `saved` is the last-persisted snapshot — what the form resets to on cancel
  // and what the read-only view reflects. The editable field states are seeded
  // from it and only written back once a save succeeds.
  const [saved, setSaved] = useState(() => toSettingsSnapshot(initialSettings));
  const [recipientMode, setRecipientMode] = useState<RecipientMode>(saved.recipientMode);
  const [onboardingSchedule, setOnboardingSchedule] = useState<Schedule>(saved.onboardingSchedule);
  const [additionalEmails, setAdditionalEmails] = useState(saved.additionalEmails);
  const [isEditing, setIsEditing] = useState(false);

  const [sendOnboarding, { loading: sending }] = useMutation(
    ADMIN_SEND_ORGANIZATION_ONBOARDING_EMAIL,
    {
      onCompleted: () => {
        confirmSend.onClose();
        toast({ title: "Onboarding email sent", status: "success" });
      },
      onError: (error) =>
        toast({ title: "Failed to send", description: error.message, status: "error" }),
    }
  );

  const [updateSettings, { loading: saving }] = useMutation(
    ADMIN_UPDATE_ORGANIZATION_NOTIFICATION_SETTINGS,
    {
      onCompleted: () => toast({ title: "Notification settings saved", status: "success" }),
      onError: (error) =>
        toast({ title: "Failed to save", description: error.message, status: "error" }),
    }
  );

  const startEditing = () => setIsEditing(true);

  const cancelEditing = () => {
    setRecipientMode(saved.recipientMode);
    setOnboardingSchedule(saved.onboardingSchedule);
    setAdditionalEmails(saved.additionalEmails);
    setIsEditing(false);
  };

  const saveSettings = async () => {
    const parsedEmails = additionalEmails
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);

    try {
      await updateSettings({
        variables: {
          data: { organizationId, recipientMode, onboardingSchedule, additionalEmails: parsedEmails },
        },
      });
      // Commit the saved snapshot and normalise the emails field to the
      // de-duplicated, trimmed form that was actually persisted.
      const normalizedEmails = parsedEmails.join(", ");
      setSaved({ recipientMode, onboardingSchedule, additionalEmails: normalizedEmails });
      setAdditionalEmails(normalizedEmails);
      setIsEditing(false);
    } catch {
      // onError already surfaced the failure; stay in edit mode so the admin
      // can retry without losing their changes.
    }
  };

  return (
    <Box bg="white" border="1px solid" borderColor="primary.150" borderRadius="md" p={5}>
      <Flex justify="space-between" align="center" mb={4}>
        <Heading as="h2" size="md">
          Notifications &amp; Onboarding
        </Heading>
        <HStack>
          <Button
            colorScheme="blue"
            variant="outline"
            isLoading={sending}
            onClick={confirmSend.onOpen}
          >
            Send onboarding email
          </Button>
          {!isEditing && (
            <Button leftIcon={<FiEdit2 />} variant="outline" onClick={startEditing}>
              Edit settings
            </Button>
          )}
        </HStack>
      </Flex>
      <Stack spacing={4}>
        <FormControl>
          <FormLabel>Notification recipients</FormLabel>
          <Select
            value={recipientMode}
            isDisabled={!isEditing}
            onChange={(event) => setRecipientMode(event.target.value as RecipientMode)}
          >
            <option value="DEFAULT">Default (contact + facility)</option>
            <option value="APPEND">Default + group inbox</option>
            <option value="REPLACE">Group inbox only</option>
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Additional notification emails (comma-separated)</FormLabel>
          <Textarea
            value={additionalEmails}
            isDisabled={!isEditing}
            onChange={(event) => setAdditionalEmails(event.target.value)}
            placeholder="ops@acme.test, facilities@acme.test"
          />
        </FormControl>
        <FormControl>
          <FormLabel>Onboarding email schedule</FormLabel>
          <Select
            value={onboardingSchedule}
            isDisabled={!isEditing}
            onChange={(event) => setOnboardingSchedule(event.target.value as Schedule)}
          >
            <option value="MANUAL">Send manually later</option>
            <option value="ON_NEXT_REQUEST_CLOSE">Send when the next request closes</option>
            <option value="IMMEDIATE">Send immediately</option>
          </Select>
        </FormControl>
        {isEditing && (
          <HStack>
            <Button colorScheme="blue" isLoading={saving} onClick={saveSettings}>
              Save settings
            </Button>
            <Button variant="ghost" isDisabled={saving} onClick={cancelEditing}>
              Cancel
            </Button>
          </HStack>
        )}
      </Stack>

      <AlertDialog
        isOpen={confirmSend.isOpen}
        leastDestructiveRef={cancelRef}
        onClose={confirmSend.onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Send onboarding email?
            </AlertDialogHeader>
            <AlertDialogBody>
              This sends the organisation&apos;s primary contact an email with a sign-in link.
              They will be able to access the business dashboard once they log in.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} variant="ghost" onClick={confirmSend.onClose}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                ml={3}
                isLoading={sending}
                onClick={() => sendOnboarding({ variables: { organizationId } })}
              >
                Send email
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};
